import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Search,
  Plus,
  Eye,
  AlertTriangle,
  Loader2,
  Users,
  Filter,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const riskColors = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-orange-100 text-orange-700 border-orange-200',
  low: 'bg-green-100 text-green-700 border-green-200',
  none: 'bg-slate-100 text-slate-700 border-slate-200',
};

const riskNames = {
  high: 'Yüksek',
  medium: 'Orta',
  low: 'Düşük',
  none: 'Normal',
};

export const StudentsPage = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    class_id: 'all',
    risk_level: 'all',
    intervention_status: 'all',
  });
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [newStudent, setNewStudent] = useState({
    first_name: '',
    last_name: '',
    class_id: '',
    grade_level: 1,
    gender: '',
    guardian_name: '',
    guardian_phone: '',
  });

  const fetchStudents = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.class_id !== 'all') params.append('class_id', filters.class_id);
      if (filters.risk_level !== 'all') params.append('risk_level', filters.risk_level);
      if (filters.intervention_status !== 'all') params.append('intervention_status', filters.intervention_status);
      if (filters.search) params.append('search', filters.search);
      
      const response = await fetch(`${API_URL}/api/students?${params}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStudents(data);
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
      toast.error('Öğrenciler yüklenirken hata oluştu');
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/classes`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setClasses(data);
      }
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/students/stats`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStudents(), fetchClasses(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchStudents();
    }
  }, [filters.class_id, filters.risk_level, filters.intervention_status]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchStudents();
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/students`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newStudent),
      });
      
      if (response.ok) {
        toast.success('Öğrenci eklendi');
        setIsAddDialogOpen(false);
        setNewStudent({
          first_name: '',
          last_name: '',
          class_id: '',
          grade_level: 1,
          gender: '',
          guardian_name: '',
          guardian_phone: '',
        });
        fetchStudents();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Öğrenci eklenemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setAddLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="students-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary-900">
            Öğrenciler
          </h1>
          <p className="text-muted-foreground">
            {stats?.total || 0} öğrenci kayıtlı
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Dışa Aktar
          </Button>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-student-btn">
                <Plus className="mr-2 h-4 w-4" />
                Öğrenci Ekle
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Yeni Öğrenci Ekle</DialogTitle>
                <DialogDescription>
                  Yeni öğrenci bilgilerini girin
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddStudent}>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">Ad</Label>
                      <Input
                        id="first_name"
                        value={newStudent.first_name}
                        onChange={(e) => setNewStudent({...newStudent, first_name: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Soyad</Label>
                      <Input
                        id="last_name"
                        value={newStudent.last_name}
                        onChange={(e) => setNewStudent({...newStudent, last_name: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="class_id">Sınıf</Label>
                      <Select
                        value={newStudent.class_id}
                        onValueChange={(value) => {
                          const cls = classes.find(c => c.class_id === value);
                          setNewStudent({
                            ...newStudent, 
                            class_id: value,
                            grade_level: cls?.grade_level || 1
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sınıf seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {classes.map(cls => (
                            <SelectItem key={cls.class_id} value={cls.class_id}>
                              {cls.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gender">Cinsiyet</Label>
                      <Select
                        value={newStudent.gender}
                        onValueChange={(value) => setNewStudent({...newStudent, gender: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="K">Kız</SelectItem>
                          <SelectItem value="E">Erkek</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guardian_name">Veli Adı</Label>
                    <Input
                      id="guardian_name"
                      value={newStudent.guardian_name}
                      onChange={(e) => setNewStudent({...newStudent, guardian_name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guardian_phone">Veli Telefonu</Label>
                    <Input
                      id="guardian_phone"
                      value={newStudent.guardian_phone}
                      onChange={(e) => setNewStudent({...newStudent, guardian_phone: e.target.value})}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    İptal
                  </Button>
                  <Button type="submit" disabled={addLoading || !newStudent.class_id}>
                    {addLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Ekle
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-primary-900">{stats.total}</div>
              <p className="text-sm text-muted-foreground">Toplam Öğrenci</p>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-red-700">{stats.by_risk_level?.high || 0}</div>
              <p className="text-sm text-red-600">Yüksek Risk</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-orange-700">{stats.by_risk_level?.medium || 0}</div>
              <p className="text-sm text-orange-600">Orta Risk</p>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-700">{stats.with_interventions || 0}</div>
              <p className="text-sm text-green-600">Aktif Müdahale</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Öğrenci ara..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="pl-10"
                data-testid="student-search"
              />
            </div>
            <Select 
              value={filters.class_id} 
              onValueChange={(value) => setFilters({...filters, class_id: value})}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Sınıf" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Sınıflar</SelectItem>
                {classes.map(cls => (
                  <SelectItem key={cls.class_id} value={cls.class_id}>
                    {cls.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select 
              value={filters.risk_level} 
              onValueChange={(value) => setFilters({...filters, risk_level: value})}
            >
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="Risk" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Riskler</SelectItem>
                <SelectItem value="high">Yüksek Risk</SelectItem>
                <SelectItem value="medium">Orta Risk</SelectItem>
                <SelectItem value="low">Düşük Risk</SelectItem>
                <SelectItem value="none">Normal</SelectItem>
              </SelectContent>
            </Select>
            <Select 
              value={filters.intervention_status} 
              onValueChange={(value) => setFilters({...filters, intervention_status: value})}
            >
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="Müdahale" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="aktif">Müdahale Var</SelectItem>
                <SelectItem value="yok">Müdahale Yok</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" variant="secondary">
              <Filter className="mr-2 h-4 w-4" />
              Filtrele
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Students Table */}
      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Öğrenci</TableHead>
                <TableHead>Sınıf</TableHead>
                <TableHead>Risk Durumu</TableHead>
                <TableHead>Risk Bayrakları</TableHead>
                <TableHead>Müdahale</TableHead>
                <TableHead className="w-[100px]">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {students.map((student) => (
                <TableRow key={student.student_id} data-testid={`student-row-${student.student_id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{student.full_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {student.gender === 'K' ? 'Kız' : 'Erkek'} • {student.grade_level}. Sınıf
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{student.class_name}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={riskColors[student.risk_level]}>
                      {student.risk_level === 'high' && <AlertTriangle className="mr-1 h-3 w-3" />}
                      {riskNames[student.risk_level]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {student.risk_flags?.map((flag, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {flag.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                      {(!student.risk_flags || student.risk_flags.length === 0) && (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {student.intervention_status ? (
                      <Badge variant="outline" className="bg-blue-100 text-blue-700">
                        <Users className="mr-1 h-3 w-3" />
                        Aktif
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" asChild>
                      <Link to={`/students/${student.student_id}`}>
                        <Eye className="h-4 w-4 mr-1" />
                        Görüntüle
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {students.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Öğrenci bulunamadı
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentsPage;
